<template>
  <div>
    <main class="container">
      <table-header
        title="Архив писем"
        :is-loading="isLoading"
        :is-empty="!emails.length"
      >
        <filter-emails @applyFilters="applyFilters" />
      </table-header>
      <div class="table-wrapper">
        <div class="table-container">
          <transition name="fade">
            <table
              v-if="!isLoading && emails.length"
              class="table interactive"
            >
              <thead>
                <tr>
                  <th
                    v-for="header in tableHeaders"
                    :key="header.key"
                    :class="[{'sortable sortable_asc': header.sortable}, getSortClass(header.key)]"
                    @click="changeSortKey(header.key, header.sortable)"
                  >
                    <div class="table__header">
                      {{ header.label }}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="it in emails"
                  :key="it.id"
                  :set="deliveryStatus = getDeliveryStatus(it)"
                >
                  <td>
                    <email-actions
                      :email="it"
                      @showDetailInfoModal="showDetailInfoModal(it.sg_Events)"
                      @showEmailTemplateModal="showEmailTemplateModal(it._id)"
                      @downloadEmail="downloadEmail(it._id)"
                    />
                  </td>
                  <td>{{ it.timestamp | parseDate }}</td>
                  <td>{{ it.event }}</td>
                  <td>
                    <span>
                      <b>to: </b>
                      <span
                        v-for="(email, index) in it.to.split(',')"
                        :key="index"
                        :set="itemStyle = getEmailDeliveryStatus(deliveryStatus, email)"
                        class="email"
                      ><span
                        v-b-tooltip="{variant: 'light', trigger: 'hover', customClass: 'email-tooltip' }"
                        :class="itemStyle.style"
                        :title="itemStyle.message"
                      >{{ email }}</span></span>
                    </span>
                    <span v-if="it.cc">
                      <br><b>cc: </b>
                      <span
                        v-for="(email, index) in it.cc.split(',')"
                        :key="index"
                        :set="itemStyle = getEmailDeliveryStatus(deliveryStatus, email)"
                        class="email"
                      ><span
                        v-b-tooltip="{variant: 'light', trigger: 'hover', customClass: 'email-tooltip' }"
                        :class="itemStyle.style"
                        :title="itemStyle.message"
                      >{{ email }}</span></span>
                    </span>
                  </td>
                  <td>{{ it.subject }}</td>
                  <td :class="getStatusClass(it.sg_Events[0].send)">
                    {{ it.sg_Events[0].send }}
                  </td>
                  <td>
                    <span :class="deliveryStatus.style">{{ deliveryStatus.message }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </transition>
        </div>
      </div>
      <table-footer
        v-if="emails.length && !isLoading"
        :current-page="currentPage"
        :per-page="perPage"
        :limit="limit"
        :total-count="totalCount"
        @pageChanged="(page) => currentPage = page"
        @limitChanged="(lim) => limit = lim"
      />
    </main>
    <detail-info-modal
      v-model="isDetailInfoModalShown"
      :detail-info="detailInfo"
    />
    <email-template-modal
      v-model="isEmailTemplateModalShown"
      :email-id="emailTemplateId"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import TableHeader from '@/components/table/TableHeader';
import TableFooter from '@/components/table/TableFooter';
import DetailInfoModal from '@/components/common/DetailInfoModal';
import FilterEmails from '@/components/emails/FilterEmails';
import EmailActions from '@/components/emails/EmailActions';
import EmailTemplateModal from '@/components/emails/EmailTemplateModal';

export default {
  name: 'EmailList',
  components: {
    TableHeader,
    TableFooter,
    DetailInfoModal,
    FilterEmails,
    EmailActions,
    EmailTemplateModal,
  },
  data: () => ({
    isLoading: true,
    emails: [],
    tableHeaders: [
      {
        key: 'detail-info',
        label: '',
        sortable: false,
      },
      {
        key: 'timestamp',
        label: 'Дата отправки',
        sortable: true,
      },
      {
        key: 'event',
        label: 'Событие',
        sortable: true,
      },
      {
        key: 'to',
        label: 'Кому',
        sortable: true,
      },
      {
        key: 'subject',
        label: 'Тема',
        sortable: true,
      },
      {
        key: 'status',
        label: 'Mailer',
        sortable: false,
      },
      {
        key: 'delivery',
        label: 'Доставка',
        sortable: false,
      },
    ],
    currentPage: 1,
    perPage: 10,
    limit: 10,
    totalCount: null,
    pageWatcherDisabled: false,
    isDetailInfoModalShown: false,
    isEmailTemplateModalShown: false,
    detailInfo: null,
    emailTemplateId: null,
    sortReversed: true,
    sortKey: 'timestamp',
    sort: '-timestamp',
    filters: {},
  }),
  computed: {
    ...mapState(['isBackgroundBlurred']),
    pages() {
      return Math.ceil(this.totalCount / this.perPage);
    },
    offset() {
      return this.limit * (this.currentPage - 1);
    },
  },
  watch: {
    limit() {
      this.pageWatcherDisabled = true;
      this.currentPage = 1;
      this.getEmails();
    },
    currentPage() {
      if (this.pageWatcherDisabled) {
        return;
      }

      this.getEmails();
    },
    isDetailInfoModalShown(value) {
      this.$store.commit('setBackgroundBlur', value);
    },
    isEmailTemplateModalShown(value) {
      this.$store.commit('setBackgroundBlur', value);
    },
  },
  created() {
    this.getEmails();
  },
  methods: {
    async getEmails() {
      this.isLoading = true;
      const today = new Date();
      const weekAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7, 0, 0, 0, 0).toISOString();

      const { items, total } = await this.$http.get('mailer/1.0/mail', {
        params: {
          limit: this.limit,
          skip: this.offset,
          sort: this.sort,
          'timestamp[lte]': today,
          'timestamp[gte]': weekAgo,
          ...this.filters,
        },
      });

      this.isLoading = false;

      this.emails = items;
      this.totalCount = total;
      this.perPage = this.limit;
      this.pageWatcherDisabled = false;
    },
    async updateEmails() {
      this.pageWatcherDisabled = true;
      this.currentPage = 1;

      this.getEmails();
    },
    showDetailInfoModal(detailInfo) {
      this.isDetailInfoModalShown = true;
      this.detailInfo = {
        data: detailInfo,
      };
    },
    showEmailTemplateModal(id) {
      this.isEmailTemplateModalShown = true;
      this.emailTemplateId = id;
    },
    async downloadEmail(id) {
      const data = await this.$http.get(`mailer/1.0/mail/${id}/eml`);
      const blob = new Blob([data], { type: 'text/html' });

      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = 'email.eml';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    getStatusClass(status) {
      return {
        'text-success': status === 'OK',
        'text-danger': status === 'FAIL',
      };
    },
    getEmailDeliveryStatus(statuses, email) {
      const lastStatus = statuses[email.toLowerCase()];

      if (!lastStatus) {
        return { style: 'unknown', message: 'Статус неизвестен' };
      }

      let message;
      let style = lastStatus.event;

      switch (lastStatus.event) {
        case 'processed':
          message = 'В процессе';
          break;
        case 'delivered':
          message = 'Доставлено';
          break;
        case 'bounce':
          message = `Bounce: ${lastStatus.status}, ${lastStatus.reason}`;
          break;
        case 'deferred':
          message = `Deferred: ${lastStatus.response}, attempt: ${lastStatus.attempts}`;
          break;
        case 'dropped':
          message = `Dropped: ${lastStatus.status}, ${lastStatus.reason}`;
          break;
        case 'blocked':
          message = `Blocked: ${lastStatus.status}, ${lastStatus.reason}`;
          break;
        default:
          message = `Неизвестный статус ${JSON.stringify(lastStatus)}`;
          style = 'unknwon';
      }

      return { style, message };
    },

    getDeliveryStatus(email) {
      const {
        to = '', cc = '', bcc = '', sg_Events: sgEvents = [],
      } = email;

      const filterEvents = ['open', 'click', 'spamreport', 'unsubscribe'];

      const deliveryStatus = {};
      sgEvents.forEach((event) => {
        // We need only events for emails
        if (!event.email) { return; }

        // Skip some extra events like open, click, etc.
        if (filterEvents.includes((event.event))) { return; }

        // Add new index email
        if (!deliveryStatus[event.email]) {
          deliveryStatus[event.email] = event;
          return;
        }

        // Update only with newer events
        if (deliveryStatus[event.email].timestamp <= event.timestamp) {
          deliveryStatus[event.email] = event;
        }
      });

      const addressList = [
        ...(to.toLowerCase().split(',')),
        ...(cc.toLowerCase().split(',')),
        ...(bcc.toLowerCase().split(',')),
      ].filter(address => (address !== ''));

      const delivered = addressList.filter((address) => ((deliveryStatus[address] || {}).event === 'delivered'));

      if (sgEvents[0].send !== 'OK') {
        deliveryStatus.style = '';
        deliveryStatus.message = '';
      } else {
        deliveryStatus.style = delivered.length === addressList.length ? 'full-delivery' : 'partial-delivery';
        deliveryStatus.message = `${delivered.length} / ${addressList.length}`;
      }

      return deliveryStatus;
    },
    applyFilters(filters) {
      this.filters = filters;
      this.pageWatcherDisabled = true;
      this.currentPage = 1;
      this.getEmails();
    },
    changeSortKey(sortKey) {
      if (this.isLoading) {
        return;
      }

      this.sortReversed = this.sortKey === sortKey ? !this.sortReversed : false;
      this.sortKey = sortKey;

      this.sort = (this.sortReversed ? '-' : '+') + this.sortKey;
      this.pageWatcherDisabled = true;
      this.currentPage = 1;
      this.getEmails();
    },
    getSortClass(sortKey) {
      return { [`sortable_active ${this.sortReversed ? 'sortable_desc' : 'sortable_asc'}`]: this.sortKey === sortKey };
    },
  },
};
</script>

<style scoped>
tr.interactive:hover {
  background-color: rgba(69, 253, 161, 0.05);
  cursor: pointer;
}

.email + .email:before {
    content: ", ";
}

.email:hover {
    outline: none;
}

.email-tooltip {
    white-space: nowrap;
}

.email .unknown {
    color: #cccccc;
    }
.email .processed {
    color: #ffff00;
    }
.email .bounce {
    color: #3333cc;
    }
.email .delivered {
    color: #00962f;
    }
.email .deferred {
    color: rgb(211, 129, 9);
    }
.email .dropped {
    color: #ff296d;
    }
.email .blocked {
        color: #ff0000;
    }

.full-delivery {
    color: #00962f;
}

.partial-delivery {
    color: #ff0000;
}
</style>
